import i18next from 'i18next';

export const AUDIT_LOG_DETAIL_TYPE = {
  TEXT: 'text',
  STORES: 'stores',
  LIST_DELTA: 'listDelta',
};

const formatQuotedName = (name) => `« ${name} »`;

const formatComposition = (composition) => {
  if (!composition || composition.type === 'NONE') {
    return i18next.t('GENERAL.NONE_VALUE');
  }

  if (composition.type === 'RECIPE') {
    return i18next.t('AUDIT_EVENTS.COMPOSITION_TYPE_RECIPE', {
      name: composition.entityName,
    });
  }

  return i18next.t('AUDIT_EVENTS.COMPOSITION_TYPE_INGREDIENT', {
    name: composition.entityName,
  });
};

const BEFORE_AFTER_FIELDS = [
  {
    beforeKey: 'nameBefore',
    afterKey: 'nameAfter',
    labelKey: 'AUDIT_EVENTS.DETAIL_NAME',
    format: formatQuotedName,
  },
  {
    beforeKey: 'compositionBefore',
    afterKey: 'compositionAfter',
    labelKey: 'AUDIT_EVENTS.DETAIL_COMPOSITION',
    format: formatComposition,
  },
];

const LIST_DELTA_FIELDS = [
  { addedKey: 'storesAdded', removedKey: 'storesRemoved' },
  { addedKey: 'cashierProductsAdded', removedKey: 'cashierProductsRemoved' },
];

/**
 * Builds the list of before/after detail blocks to render under an audit log entry, driven purely
 * by what the `logs` jsonb contains. Keeps the History/Changelog components domain-agnostic: any
 * event whose logs carry these keys gets the matching detail rendered, no per-event branching.
 *
 * @param {object | undefined} logs - The audit log `logs` jsonb.
 * @returns {Array<{ type: string, labelKey?: string, before?: any, after?: any, itemsAdded?: any[], itemsRemoved?: any[] }>}
 */
export const buildAuditLogDetails = (logs) => {
  if (!logs) {
    return [];
  }

  const details = [];

  const listDeltaField = LIST_DELTA_FIELDS.find(
    ({ addedKey, removedKey }) => logs[addedKey] !== undefined || logs[removedKey] !== undefined,
  );

  if (listDeltaField) {
    details.push({
      type: AUDIT_LOG_DETAIL_TYPE.LIST_DELTA,
      itemsAdded: logs[listDeltaField.addedKey] ?? [],
      itemsRemoved: logs[listDeltaField.removedKey] ?? [],
    });

    return details;
  }

  BEFORE_AFTER_FIELDS.forEach(({ beforeKey, afterKey, labelKey, format }) => {
    if (logs[beforeKey] === undefined || logs[afterKey] === undefined) {
      return;
    }

    details.push({
      type: AUDIT_LOG_DETAIL_TYPE.TEXT,
      ...(labelKey ? { labelKey } : {}),
      before: format(logs[beforeKey]),
      after: format(logs[afterKey]),
    });
  });

  if (logs.storesAfter !== undefined && logs.storesAfter.length > 0) {
    details.push({
      type: AUDIT_LOG_DETAIL_TYPE.STORES,
      labelKey: 'AUDIT_EVENTS.DETAIL_ESTABLISHMENTS',
      after: logs.storesAfter,
    });
  }

  return details;
};
